import { createContext, useContext, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { getMe } from '../services/api'
import { getOrgVocabulary, DEFAULT_ORG_TYPE, type OrgType, type OrgVocabulary } from '../constants/orgTerminology'

interface OrgTypeContextType {
  orgType: OrgType
  vocab: OrgVocabulary
}

const OrgTypeContext = createContext<OrgTypeContextType | undefined>(undefined)

export function OrgTypeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth()

  const { data } = useQuery({
    queryKey: ['me-org-type', user?.id],
    queryFn: getMe,
    staleTime: 10 * 60 * 1000,
    enabled: isAuthenticated,
  })

  const activeMembership =
    data?.user?.org_memberships?.find((m) => m.org_id === user?.active_org_id) ??
    data?.user?.org_memberships?.[0]

  const orgType = (activeMembership?.org_type || DEFAULT_ORG_TYPE) as OrgType
  const vocab = getOrgVocabulary(orgType)

  return (
    <OrgTypeContext.Provider value={{ orgType, vocab }}>
      {children}
    </OrgTypeContext.Provider>
  )
}

export function useOrgType(): OrgTypeContextType {
  const ctx = useContext(OrgTypeContext)
  // Return vc_fund defaults when called outside the provider tree (e.g. in tests)
  return ctx ?? { orgType: DEFAULT_ORG_TYPE, vocab: getOrgVocabulary(DEFAULT_ORG_TYPE) }
}
